import React, { useMemo } from "react";
import { Drawer, Box, Typography, Paper, Chip, Button, IconButton } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { makeStyles } from "@material-ui/core/styles";

import { useQuery, useTime } from "../../hooks";
import { isEmpty } from "../../utils/helpers";
import { formatDateTime } from "../../utils/dateUtils";

const useStyles = makeStyles(theme => ({
  drawerPaper: {
    width: 600,
    minWidth: 600
  },
  header: {
    padding: theme.spacing(2),
    borderBottom: "1px solid #e0e0e0",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },
  content: {
    padding: theme.spacing(1.5),
    overflowY: "auto",
    flex: 1
  },
  footer: {
    padding: theme.spacing(1),
    borderTop: "1px solid #e0e0e0",
    textAlign: "right"
  },
  logCard: {
    padding: theme.spacing(1.25, 1.5),
    marginBottom: theme.spacing(1.25),
    borderLeft: "3px solid #1976d2"
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1)
  },
  byText: {
    marginTop: 4,
    fontSize: 12,
    color: "#666"
  },
  dateText: {
    fontSize: 12,
    color: "#777"
  },
  changeRow: {
    display: "grid",
    gridTemplateColumns: "110px minmax(0, 1fr)",
    gap: theme.spacing(1),
    alignItems: "center",
    padding: "5px 0",
    borderTop: "1px solid #eee"
  },
  valueWrap: {
    fontSize: 13,
    overflowX: "auto",
    overflowY: "hidden",
    whiteSpace: "nowrap",
    maxWidth: "100%",
    paddingBottom: 2
  },
  fieldName: {
    fontWeight: 600,
    fontSize: 13
  },

  oldValue: {
    color: "#d32f2f",
    background: "#ffebee",
    padding: "2px 6px",
    borderRadius: 4
  },
  newValue: {
    color: "#2e7d32",
    background: "#e8f5e9",
    padding: "2px 6px",
    borderRadius: 4
  },
  arrow: {
    margin: "0 6px",
    color: "#777"
  }
}));

const FIELD_LABELS = {
  fullName: "Full Name",
  username: "User Name",
  email: "Email",
  mobile: "Mobile",
  roles: "Roles",
  warehouseIds: "Warehouse",
  customerIds: "Customer",
  unitIds: "Units",
  enabled: "Enabled",
  deActivatedAt: "Deactivated At",
  deActivatedBy: "Deactivated By"
};

const DATE_FIELDS = ["createdAt", "modifiedAt", "deActivatedAt"];
const TRACKED_FIELDS = Object.keys(FIELD_LABELS);

function isValidDateValue(value) {
  if (value === null || value === undefined || value === "" || value === "-") {
    return false;
  }

  const date = new Date(value);
  return !isNaN(date.getTime());
}

function safeFormatDateTime(value, utcOffset) {
  return isValidDateValue(value) ? formatDateTime(value, utcOffset) : "-";
}

function distinctCsv(value) {
  if (value === null || value === undefined || value === "" || value === "-") {
    return "-";
  }

  return String(value)
    .split(",")
    .map(v => v.trim())
    .filter(Boolean)
    .filter((v, index, arr) => arr.indexOf(v) === index)
    .join(",");
}

function normalize(field, value, utcOffset) {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (DATE_FIELDS.includes(field)) {
    return safeFormatDateTime(value, utcOffset);
  }

  if (["roles", "warehouseIds", "customerIds", "unitIds"].includes(field)) {
    return distinctCsv(value);
  }

  return String(value);
}
function getAction(log, changes) {
  if (log.revtype === 0) return "Created";
  if (log.revtype === 2) return "Deleted";

  if (changes.some(c => c.field === "enabled")) {
    return log.enabled ? "Activated" : "Deactivated";
  }

  return "Updated";
}

function buildHistoryRows(logs, utcOffset) {
  if (isEmpty(logs)) return [];

  return logs.map((current, index) => {
    const previous = index > 0 ? logs[index - 1] : null;
    const changes = [];

    TRACKED_FIELDS.forEach(field => {
      const oldValue = previous ? previous[field] : "-";
      const newValue = current[field];

      const normalizedOldValue = normalize(field, oldValue, utcOffset);
      const normalizedNewValue = normalize(field, newValue, utcOffset);

      if (!previous) {
        if (newValue !== null && newValue !== undefined && newValue !== "") {
          changes.push({
            field,
            label: FIELD_LABELS[field],
            oldValue: "-",
            newValue: normalizedNewValue
          });
        }
      } else if (normalizedOldValue !== normalizedNewValue) {
        changes.push({
          field,
          label: FIELD_LABELS[field],
          oldValue: normalizedOldValue,
          newValue: normalizedNewValue
        });
      }
    });

    return {
      ...current,
      changes,
      action: getAction(current, changes)
    };
  });
}

const UserHistoryDrawer = ({ open, close, id }) => {
  const classes = useStyles();
  const { data: userLogs = [] } = useQuery(id && { url: `users/${id}/log` });
  const { utc_offset } = useTime();

  const rows = useMemo(() => {
    const historyRows = buildHistoryRows(userLogs, utc_offset);
    return [...historyRows].reverse();
  }, [userLogs, utc_offset]);

  return (
    <Drawer anchor="right" open={open} onClose={close} classes={{ paper: classes.drawerPaper }}>
      <Box className={classes.header}>
        <Typography variant="h6">User History</Typography>

        <IconButton size="small" onClick={close}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box className={classes.content}>
        {isEmpty(rows) && <Typography color="textSecondary">No history available.</Typography>}

        {rows.map(log => (
          <Paper key={`${log.id}-${log.rev}`} className={classes.logCard} elevation={1}>
            <Box className={classes.cardHeader}>
              <Box>
                <Chip size="small" color="primary" label={log.action} />
                <Typography className={classes.byText}>{log.modifiedBy || log.createdBy || "-"}</Typography>
              </Box>

              <Typography className={classes.dateText}>{safeFormatDateTime(log.modifiedAt || log.createdAt, utc_offset)}</Typography>
            </Box>

            {isEmpty(log.changes) ? (
              <Typography variant="body2" color="textSecondary">
                No field-level change detected.
              </Typography>
            ) : (
              log.changes.map(change => (
                <Box key={change.field} className={classes.changeRow}>
                  <Typography className={classes.fieldName}>{change.label}</Typography>

                  <Box className={classes.valueWrap}>
                    <span className={classes.oldValue}>{change.oldValue}</span>
                    <span className={classes.arrow}>→</span>
                    <span className={classes.newValue}>{change.newValue}</span>
                  </Box>
                </Box>
              ))
            )}
          </Paper>
        ))}
      </Box>

      <Box className={classes.footer}>
        <Button onClick={close} color="primary">
          Close
        </Button>
      </Box>
    </Drawer>
  );
};

export default UserHistoryDrawer;
